/**
 * Admin — the January–August 2026 monthly closeout table (card 4939).
 *
 * ⛔ THIS COMPONENT DECIDES NOTHING. Every rule it needs — which order count is the right one,
 * when a money cell is unknown, how cents become text — is imported from
 * `web/utils/monthlyCloseoutModel.js`. That is not style: `jest.config.js` has no jsx transform,
 * so a rule written here is a rule no test can reach. If you are about to add an `if` to this
 * file, add it to the model and import the result.
 *
 * 🔑 The table scrolls rather than reflowing. Nine columns of money cannot collapse into a
 * phone-width card view without the reader losing which figure belongs to which month, so the
 * house `.tableScroll` idiom from RevenueCalculationPage.module.css is reused as-is.
 */

import {
  formatCloseoutMoney,
  formatCloseoutCount,
  CLOSEOUT_UNRESOLVED_LABEL
} from '../../utils/monthlyCloseoutModel';
import styles from './MonthlyCloseoutTable.module.css';

/** The columns, in render order. Pure data so the header and the body cannot drift apart. */
const COLUMNS = Object.freeze([
  { key: 'label', label: 'Month', numeric: false },
  { key: 'orderCount', label: 'Orders', numeric: true },
  { key: 'collectedGrossCents', label: 'Gross (collected)', numeric: true, money: true },
  { key: 'uncollectedCount', label: 'Uncollected', numeric: true },
  { key: 'uncollectedGrossCents', label: 'Uncollected $', numeric: true, money: true },
  { key: 'unresolvedCount', label: 'Unresolved', numeric: true },
  { key: 'totalExpenseCents', label: 'Total expense', numeric: true, money: true },
  { key: 'profitCents', label: 'Profit', numeric: true, money: true },
  { key: 'sourceQuality', label: 'Source quality', numeric: false }
]);

/** PURE. One row + one column → the text in that cell. */
const cellText = (row, column) => {
  if (column.key === 'label') return row.label;
  if (column.key === 'sourceQuality') {
    const { verified, estimated, unresolved } = row.sourceQualityCounts;
    return `${row.sourceQualityLabel ?? 'unknown'} · ${verified} / ${estimated} / ${unresolved}`;
  }
  return column.money ? formatCloseoutMoney(row[column.key]) : formatCloseoutCount(row[column.key]);
};

const MonthlyCloseoutTable = ({ rows = [], populationBasis = null }) => (
  <div data-testid="monthly-closeout">
    <p className={styles.note} data-testid="monthly-closeout-basis">
      Eight UTC calendar months, each read from
      {' '}<code>/admin/revenue-calculation/summary</code> over its own window.
      {populationBasis ? ` Orders column population: ${populationBasis}.` : ''}
      {' '}Gross is on the <strong>collected</strong> basis — money with a settled payment row
      behind it — so it is deliberately smaller than the all-status headline above.
      {' '}A month carrying an unresolved cost line publishes
      {' '}<strong>{CLOSEOUT_UNRESOLVED_LABEL}</strong> for expense and profit rather than a
      total that silently counted the unknown line as zero.
    </p>

    <div className={styles.tableScroll}>
      <table className={styles.closeoutTable} data-testid="monthly-closeout-table">
        <thead>
          <tr>
            {COLUMNS.map((column) => (
              <th
                key={column.key}
                scope="col"
                className={column.numeric ? styles.numericHead : undefined}
              >
                {column.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.key} data-testid={`monthly-closeout-row-${row.key}`}>
              {COLUMNS.map((column) => {
                const text = cellText(row, column);
                const isUnresolved = text === CLOSEOUT_UNRESOLVED_LABEL;
                const className = [
                  column.numeric ? styles.numericCell : styles.monthCell,
                  isUnresolved ? styles.unresolvedCell : ''
                ].filter(Boolean).join(' ');
                return column.key === 'label' ? (
                  <th key={column.key} scope="row" className={className}>{text}</th>
                ) : (
                  <td key={column.key} className={className}>{text}</td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

export default MonthlyCloseoutTable;
